<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDateTime } from '@yuksalish/ui'
import ProductPickerModal from '../components/ProductPickerModal.vue'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatAmount, formatMoney } from '../utils/format'
import type {
  CostingComponentResult,
  CostingSnapshot,
  PricelistDetail,
  PricelistOption,
  ProductOption,
} from '../types/finance'

const { t } = useI18n()
const { listCostings, listPricelists, getPricelist } = useFinanceApi()

const PER_PAGE = 20

const pickerOpen = ref(false)
const product = ref<ProductOption | null>(null)
const snapshots = ref<CostingSnapshot[]>([])
const selectedId = ref<string>('')
const loading = ref(false)
const error = ref('')

const page = ref(1)
const total = ref(0)
const totalPages = ref(1)

const productionType = ref('')

const pricelists = ref<PricelistOption[]>([])
const pricelistId = ref<string>('')
const pricelistDetail = ref<PricelistDetail | null>(null)
const priceError = ref('')

/** Newest snapshot first — `GET /costings` already orders by created_at DESC. */
const latest = computed(() => snapshots.value[0] ?? null)

const selected = computed(
  () => snapshots.value.find((snapshot) => String(snapshot.id) === selectedId.value) ?? latest.value,
)

const previousSnapshot = computed(() => {
  if (!selected.value) return null
  const index = snapshots.value.findIndex((snapshot) => snapshot.id === selected.value?.id)
  return index >= 0 ? snapshots.value[index + 1] ?? null : null
})

const variance = computed(() => {
  const current = selected.value?.output_snapshot?.summary?.final_cost
  const previous = previousSnapshot.value?.output_snapshot?.summary?.final_cost
  if (current === undefined || previous === undefined) return null
  const delta = Number(current) - Number(previous)
  return {
    delta,
    rate: Number(previous) === 0 ? null : (delta / Number(previous)) * 100,
  }
})

const components = computed<CostingComponentResult[]>(
  () => selected.value?.output_snapshot?.components ?? [],
)

const costCurrency = computed(() => selected.value?.output_snapshot?.currency || '')

const priceItem = computed(() => {
  if (!product.value || !pricelistDetail.value?.items) return null
  return (
    pricelistDetail.value.items.find(
      (item) => String(item.product_id) === String(product.value?.id),
    ) ?? null
  )
})

const latestCost = computed(() => Number(latest.value?.output_snapshot?.summary?.final_cost ?? 0))

const margin = computed(() => {
  if (!priceItem.value || !latest.value) return null
  const price = Number(priceItem.value.price)
  const cost = latestCost.value
  return {
    amount: price - cost,
    rate: price === 0 ? null : ((price - cost) / price) * 100,
  }
})

const currencyMismatch = computed(
  () => !!priceItem.value && !!costCurrency.value && priceItem.value.currency_code !== costCurrency.value,
)

const rangeLabel = computed(() => {
  if (!total.value) return ''
  const from = (page.value - 1) * PER_PAGE + 1
  const to = Math.min(page.value * PER_PAGE, total.value)
  return t('finance.common.showing', { from, to, total: total.value })
})

async function loadSnapshots() {
  if (!product.value) return
  loading.value = true
  error.value = ''
  try {
    const result = await listCostings({
      product_id: product.value.id,
      production_type: productionType.value.trim() || undefined,
      page: page.value,
      per_page: PER_PAGE,
    })
    snapshots.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
    selectedId.value = result.items[0] ? String(result.items[0].id) : ''
  } catch (cause) {
    error.value = formatApiError(cause)
    snapshots.value = []
    total.value = 0
    totalPages.value = 1
  } finally {
    loading.value = false
  }
}

async function loadPricelists() {
  try {
    pricelists.value = await listPricelists()
    if (!pricelistId.value && pricelists.value.length) {
      const base = pricelists.value.find((entry) => entry.is_base === 1)
      pricelistId.value = String(base?.id ?? pricelists.value[0].id)
    }
    await loadPricelistDetail()
  } catch {
    pricelists.value = []
  }
}

async function loadPricelistDetail() {
  pricelistDetail.value = null
  priceError.value = ''
  if (!pricelistId.value) return
  try {
    pricelistDetail.value = await getPricelist(pricelistId.value)
  } catch (cause) {
    priceError.value = formatApiError(cause)
  }
}

/** The costing endpoint requires a product, so the picker drives the page. */
async function onProductSelected(next: ProductOption) {
  pickerOpen.value = false
  product.value = next
  page.value = 1
  await loadSnapshots()
}

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
  void loadSnapshots()
}

watch(pricelistId, () => {
  void loadPricelistDetail()
})

watch(productionType, () => {
  page.value = 1
  void loadSnapshots()
})

onMounted(() => {
  void loadPricelists()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.costing.title') }}</h2>
  </div>

  <div class="toolbar">
    <button class="btn-secondary" type="button" @click="pickerOpen = true">
      {{ product ? product.name : t('finance.costing.selectProduct') }}
    </button>
    <input
      v-model="productionType"
      class="search-input"
      type="search"
      :placeholder="t('finance.costing.productionTypePlaceholder')"
    >
    <span class="spacer" />
    <div class="summary">
      <span v-if="product" class="cell-code">{{ product.sku || '' }}</span>
      <span v-if="product">{{ t('finance.common.rows', { count: total }) }}</span>
    </div>
  </div>

  <div v-if="!product" class="state-message">
    <p>{{ t('finance.costing.productPrompt') }}</p>
    <button class="btn-primary" type="button" @click="pickerOpen = true">
      {{ t('finance.costing.selectProduct') }}
    </button>
  </div>

  <div v-else-if="loading && snapshots.length === 0" class="state-message">
    <p>{{ t('finance.common.loading') }}</p>
  </div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="loadSnapshots">{{ t('finance.common.retry') }}</button>
  </div>

  <div v-else-if="snapshots.length === 0" class="state-message">
    <p>{{ t('finance.costing.empty') }}</p>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card accent">
        <span class="summary-label">{{ t('finance.costing.standardCost') }}</span>
        <span class="summary-value">
          {{ formatMoney(latestCost, costCurrency) }}
        </span>
        <span class="summary-label">
          {{ formatDisplayDateTime(latest?.created_at) }}
        </span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.costing.baseCost') }}</span>
        <span class="summary-value">
          {{ formatAmount(latest?.output_snapshot?.summary?.base_cost ?? 0) }}
        </span>
        <span class="summary-label">
          {{ t('finance.costing.wasteCost') }}:
          {{ formatAmount(latest?.output_snapshot?.summary?.waste_cost ?? 0) }}
        </span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.costing.profitCost') }}</span>
        <span class="summary-value">
          {{ formatAmount(latest?.output_snapshot?.summary?.profit_cost ?? 0) }}
        </span>
        <span class="summary-label">
          {{ t('finance.costing.costBeforeProfit') }}:
          {{ formatAmount(latest?.output_snapshot?.summary?.cost_before_profit ?? 0) }}
        </span>
      </div>
      <div class="summary-card" :class="{ attention: variance ? variance.delta > 0 : false }">
        <span class="summary-label">{{ t('finance.costing.variance') }}</span>
        <span class="summary-value">
          {{ variance ? formatAmount(variance.delta) : '—' }}
        </span>
        <span class="summary-label">
          {{ variance && variance.rate !== null ? `${variance.rate.toFixed(1)}%` : t('finance.costing.noVariance') }}
        </span>
      </div>
    </div>

    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.costing.calculatedAt') }}</th>
            <th>{{ t('finance.costing.productionType') }}</th>
            <th>{{ t('finance.costing.version') }}</th>
            <th>{{ t('finance.costing.calculatedBy') }}</th>
            <th class="cell-num">{{ t('finance.costing.baseCost') }}</th>
            <th class="cell-num">{{ t('finance.costing.wasteCost') }}</th>
            <th class="cell-num">{{ t('finance.costing.costBeforeProfit') }}</th>
            <th class="cell-num">{{ t('finance.costing.profitCost') }}</th>
            <th class="cell-num">{{ t('finance.costing.finalCost') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="snapshot in snapshots"
            :key="snapshot.id"
            :class="{ 'is-selected': String(snapshot.id) === String(selected?.id) }"
            style="cursor: pointer"
            @click="selectedId = String(snapshot.id)"
          >
            <td class="cell-date">{{ formatDisplayDateTime(snapshot.created_at) }}</td>
            <td>{{ snapshot.production_type || '—' }}</td>
            <td class="cell-code">#{{ snapshot.version_id }}</td>
            <td>{{ snapshot.created_by_user_name || '—' }}</td>
            <td class="cell-num">{{ formatAmount(snapshot.output_snapshot?.summary?.base_cost ?? 0) }}</td>
            <td class="cell-num">{{ formatAmount(snapshot.output_snapshot?.summary?.waste_cost ?? 0) }}</td>
            <td class="cell-num">
              {{ formatAmount(snapshot.output_snapshot?.summary?.cost_before_profit ?? 0) }}
            </td>
            <td class="cell-num">{{ formatAmount(snapshot.output_snapshot?.summary?.profit_cost ?? 0) }}</td>
            <td class="cell-num">
              {{ formatMoney(snapshot.output_snapshot?.summary?.final_cost ?? 0, snapshot.output_snapshot?.currency) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ rangeLabel }}</span>
      <button :disabled="page <= 1 || loading" @click="goToPage(page - 1)">
        {{ t('finance.common.previous') }}
      </button>
      <span>{{ t('finance.common.page', { page, total: totalPages }) }}</span>
      <button :disabled="page >= totalPages || loading" @click="goToPage(page + 1)">
        {{ t('finance.common.next') }}
      </button>
    </div>

    <div class="detail-grid" style="margin-top: 18px">
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('finance.costing.components') }}</h3>
          <span class="empty-inline">{{ formatDisplayDateTime(selected?.created_at) }}</span>
        </div>
        <div v-if="components.length" class="table-scroll">
          <table class="data-table compact">
            <thead>
              <tr>
                <th>{{ t('finance.costing.component') }}</th>
                <th>{{ t('finance.costing.componentType') }}</th>
                <th>{{ t('finance.costing.group') }}</th>
                <th class="cell-num">{{ t('finance.costing.quantityPerUnit') }}</th>
                <th class="cell-num">{{ t('finance.costing.sourceUnitCost') }}</th>
                <th class="cell-num">{{ t('finance.costing.amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(component, index) in components" :key="`${component.name}-${index}`">
                <td class="cell-name">{{ component.name }}</td>
                <td>{{ component.type }}</td>
                <td>{{ component.group || '—' }}</td>
                <td class="cell-num">
                  {{ component.quantity_per_unit !== null && component.quantity_per_unit !== undefined
                    ? formatAmount(component.quantity_per_unit, 4)
                    : '—' }}
                </td>
                <td class="cell-num">
                  {{ component.source_unit_cost !== null && component.source_unit_cost !== undefined
                    ? formatAmount(component.source_unit_cost, 4)
                    : '—' }}
                </td>
                <td class="cell-num">
                  {{ formatMoney(component.amount, selected?.output_snapshot?.currency) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty-inline">{{ t('finance.costing.noComponents') }}</p>
      </section>

      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('finance.costing.marginAnalysis') }}</h3>
        </div>
        <div class="field">
          <label for="costing-pricelist">{{ t('finance.costing.pricelist') }}</label>
          <select id="costing-pricelist" v-model="pricelistId" :disabled="pricelists.length === 0">
            <option value="">{{ t('finance.costing.noPricelist') }}</option>
            <option v-for="entry in pricelists" :key="entry.id" :value="entry.id">
              {{ entry.code ? `${entry.code} — ` : '' }}{{ entry.name }}
            </option>
          </select>
        </div>

        <p v-if="priceError" class="error-inline">{{ priceError }}</p>

        <table v-else-if="priceItem" class="data-table compact" style="margin-top: 12px">
          <tbody>
            <tr>
              <td class="field-label">{{ t('finance.costing.price') }}</td>
              <td class="field-value cell-num">
                {{ formatMoney(priceItem.price, priceItem.currency_code) }}
              </td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.costing.finalCost') }}</td>
              <td class="field-value cell-num">{{ formatMoney(latestCost, costCurrency) }}</td>
            </tr>
            <tr v-if="margin && !currencyMismatch">
              <td class="field-label">{{ t('finance.costing.margin') }}</td>
              <td class="field-value cell-num">{{ formatAmount(margin.amount) }}</td>
            </tr>
            <tr v-if="margin && !currencyMismatch">
              <td class="field-label">{{ t('finance.costing.marginRate') }}</td>
              <td class="field-value cell-num">
                {{ margin.rate === null ? '—' : `${margin.rate.toFixed(1)}%` }}
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="empty-inline">{{ t('finance.costing.noPrice') }}</p>
        <p v-if="priceItem && currencyMismatch" class="empty-inline">
          {{ t('finance.costing.currencyMismatch') }}
        </p>
      </section>
    </div>
  </template>

  <ProductPickerModal
    :open="pickerOpen"
    :selected-id="product?.id ?? null"
    @close="pickerOpen = false"
    @select="onProductSelected"
  />
</template>
